import { useUIColors } from '@/core/hooks';
import React, { useEffect, useRef } from 'react';
import { Animated, Easing, Pressable, StyleSheet, View } from 'react-native';

export interface SwitchProps {
    /**
     * Whether the switch is on or off
     */
    value: boolean;
    /**
     * Callback when the switch value changes
     */
    onValueChange: (value: boolean) => void;
    /**
     * Whether the switch is disabled
     */
    disabled?: boolean;
}

/**
 * Custom Switch component matching Figma design exactly
 * Built from scratch using Animated API for smooth animations
 * Following LanguageSwitch animation pattern
 * 
 * Figma Specifications:
 * - Size: 38x20 (from Figma: w-[38px] h-[20px])
 * - Border: 2px solid vectorsIconGreyscale (#333333)
 * - Border radius: 10px
 * - Track colors: backgroundLevel4 (off) / fontPrimary (on)
 * - Thumb: dark circle (14px diameter, #333333)
 * - Padding: 3px horizontal, 2px vertical inside border
 */
export function Switch({ value, onValueChange, disabled = false }: SwitchProps) {
    const colors = useUIColors();

    // Animation setup following LanguageSwitch pattern
    const thumbPosition = useRef(new Animated.Value(value ? 1 : 0)).current;
    const animationRef = useRef<Animated.CompositeAnimation | null>(null);

    // Animate when value changes
    useEffect(() => {
        const targetValue = value ? 1 : 0;

        // Cancel any ongoing animation to prevent lag
        if (animationRef.current) {
            animationRef.current.stop();
        }

        // Start new animation
        animationRef.current = Animated.timing(thumbPosition, {
            toValue: targetValue,
            duration: 200,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: false, // Can't use native driver with translateX
        });

        animationRef.current.start(() => {
            animationRef.current = null;
        });

        // Cleanup on unmount
        return () => {
            if (animationRef.current) {
                animationRef.current.stop();
                animationRef.current = null;
            }
        };
    }, [value, thumbPosition]);

    const handlePress = () => {
        if (!disabled) {
            onValueChange(!value);
        }
    };

    // Calculate dimensions from Figma
    // Container: 38x20
    // Border: 2px all around
    // Padding: 3px horizontal, 2px vertical
    // Inner space after border and padding: 28x12
    // Thumb: 14px diameter
    // Thumb travel distance: 28 - 14 = 14px
    const CONTAINER_WIDTH = 38;
    const CONTAINER_HEIGHT = 20;
    const BORDER_WIDTH = 2;
    const PADDING_HORIZONTAL = 3;
    const PADDING_VERTICAL = 2;
    const THUMB_SIZE = 14;

    const innerWidth = CONTAINER_WIDTH - (BORDER_WIDTH * 2) - (PADDING_HORIZONTAL * 2); // 38 - 4 - 6 = 28
    const thumbTravel = innerWidth - THUMB_SIZE; // 28 - 14 = 14

    const translateX = thumbPosition.interpolate({
        inputRange: [0, 1],
        outputRange: [0, thumbTravel],
    });

    return (
        <Pressable
            onPress={handlePress}
            disabled={disabled}
            style={({ pressed }) => [
                styles.container,
                {
                    borderColor: colors.vectorsIconGreyscale,
                    // Off: light grey background, On: dark background
                    backgroundColor: value ? colors.fontPrimary : colors.white,
                    opacity: disabled ? 0.5 : pressed ? 0.9 : 1,
                },
            ]}
            accessibilityRole="switch"
            accessibilityState={{ checked: value }}
        >
            <View style={styles.track}>
                <Animated.View
                    pointerEvents="none"
                    style={[
                        styles.thumb,
                        {
                            width: THUMB_SIZE,
                            height: THUMB_SIZE,
                            // Thumb: dark when off (on light bg), white when on (on dark bg)
                            backgroundColor: value ? colors.white : colors.vectorsIconGreyscale,
                            transform: [{ translateX }],
                        },
                    ]}
                />
            </View>
        </Pressable>
    );
}

const styles = StyleSheet.create({
    container: {
        width: 38,
        height: 20,
        borderRadius: 10,
        borderWidth: 2,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 3,
        paddingVertical: 2,
        position: 'relative',
        // Allow thumb to render fully without clipping on high-density screens
        overflow: 'visible',
    },
    track: {
        width: 28, // 38 - (2*2) - (3*2) = 28
        height: 12, // 20 - (2*2) - (2*2) = 12
        justifyContent: 'center',
        alignItems: 'flex-start',
        position: 'relative',
    },
    thumb: {
        borderRadius: 7, // 14/2 = 7
        position: 'absolute',
        left: 0,
    },
});

export default Switch;

